import React, { useEffect, useState } from "react";
import { useStores } from "../../../use-stores";
import { useNotification } from "../../../utils/NotificationsContext";
import StockTable from "../../components/order/stock/StockTable";
import { Box } from "@mui/material";
import { GetStockParams } from "../../../models/Stock/GetStockParams";
import { useLocation, useNavigate } from "react-router-dom";
import { CircularProgress, Container } from "@mui/material";
import PageTitle from "../../components/common/pages/PageTitle";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import PermissionBlockLabel from "../../components/common/pop-up/PermissionBlockLabel ";

function StockCheckRequests() {
  const { stockStore, userStore } = useStores();
  const { showError } = useNotification();

  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search);

  const initialParams: GetStockParams = {
    page_no: searchParams.get("page")
      ? parseInt(searchParams.get("page") as string, 10)
      : 1,
    limit: searchParams.get("limit")
      ? parseInt(searchParams.get("limit") as string, 10)
      : 30,
    sortBy: searchParams.get("sort_by") || "created_date",
    search_text: searchParams.get("search_text") || "",
  };

  const [params, setParams] = useState(initialParams);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (userStore.hasPermission(EmployeeRoles.VIEW_ORDER)) {
      setIsLoading(true);
      stockStore
        .getStockList(params)
        .catch((reason) =>
          showError(
            "Error!",
            reason?.response?.data?.message ||
              "Something went wrong! Please try again later."
          )
        )
        .finally(() => {
          setIsLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, stockStore, showError]);

  useEffect(() => {
    const updateQueryParams = () => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value && (!Array.isArray(value) || value.length > 0)) {
          const paramValue = Array.isArray(value)
            ? value.join(",")
            : value.toString();
          searchParams.set(key, paramValue);
        }
      });

      const queryString = searchParams.toString();
      if (queryString) {
        navigate({ search: queryString }, { replace: true });
      }
    };

    updateQueryParams();
  }, [params, navigate]);

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setParams((prev) => ({
      ...prev,
      page_no: newPage,
    }));
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setParams((prev) => ({
      ...prev,
      limit: newLimit,
      page_no: 1,
    }));
  };

  return (
    <Container maxWidth="xl">
      <PageTitle title="Stock Check Request List" />
      {userStore.hasPermission(EmployeeRoles.VIEW_ORDER) ? (
        <>
          {isLoading ? (
            <Container
              sx={{
                minHeight: "60vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </Container>
          ) : (
            <Box>
              {stockStore?.stocks?.length > 0 ? (
                <StockTable
                  stocks={stockStore.stocks || []}
                  limit={params.limit!}
                  page_no={params.page_no}
                  total_count={stockStore.total_count}
                  total_pages={stockStore.total_pages}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  orderBy={params.sortBy}
                  orderDirection={params.sortType}
                />
              ) : (
                <p>No Stock Request</p>
              )}
            </Box>
          )}
        </>
      ) : (
        <PermissionBlockLabel />
      )}
    </Container>
  );
}

export default StockCheckRequests;
